import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ProfileSwitchState {
  fetched: boolean;
  loading: boolean;
  entities: {
    myEntities: ProfileSwitchTableData;
    corporateServiceProviders: ProfileSwitchTableDataWithDropdown;
    groupOfCompanies: ProfileSwitchTableDataWithDropdown;
    insolvencyPractitioners: ProfileSwitchTableDataWithDropdown;
    officialReceivers: ProfileSwitchTableDataWithDropdown;
    officialReceiver: ProfileSwitchTableData;
  };
  uiState: {
    tabIndex: number;
    tabKeys: (ProfileTypeKeys | 'officialReceiver')[];
  };
}

export interface ProfileRecord {
  uen: string;
  uenName: string;
  todoCount?: number;
  hasHighPriority?: boolean;
  managedEntities?: ProfileRecord[];
}

export type ProfileTypeKeys = keyof ProfileSwitchState['entities'];

export type ProfileEntitiesObject = Partial<
  Record<ProfileTypeKeys, ProfileRecord[]>
>;

type ProfileSwitchTableData = {
  loading: boolean;
  search: string;
  data: ProfileRecord[] | null;
  pagination: ProfilePagination;
};

export type ProfileSwitchTableDataWithDropdown = ProfileSwitchTableData & {
  parentEntities: ProfileRecord[] | null;
  selectedUen: string | null;
};

export type ProfilePagination = {
  sortBy: 'lastUpdated' | 'alphabetical';
  sortOrder: 'desc' | 'asc';
  pageSize: number;
  pageNumber: number;
  items: number;
};

export type PaginationRequest = Omit<ProfilePagination, 'items'>;

type KeyedAction<T, K = ProfileTypeKeys> = PayloadAction<{
  key: K;
  value: T;
}>;

export const initialPagination: ProfilePagination = {
  sortBy: 'lastUpdated',
  sortOrder: 'desc',
  pageSize: 10,
  pageNumber: 0,
  items: 0,
};

export const initialTableData = {
  loading: false,
  search: '',
  data: null,
  pagination: initialPagination,
};

export const initialTableDataWithDropdown = {
  ...initialTableData,
  parentEntities: null,
  selectedUen: null,
};

export const initialProfileSwitchState: ProfileSwitchState = {
  fetched: false,
  loading: false,
  entities: {
    myEntities: {
      ...initialTableData,
      pagination: {
        ...initialPagination,
        sortBy: 'alphabetical',
        sortOrder: 'asc',
      },
    },
    corporateServiceProviders: initialTableDataWithDropdown,
    groupOfCompanies: initialTableDataWithDropdown,
    insolvencyPractitioners: initialTableDataWithDropdown,
    officialReceivers: initialTableDataWithDropdown,
    officialReceiver: initialTableData,
  },
  uiState: {
    tabIndex: -1,
    tabKeys: [],
  },
};

export const profileSwitchSlice = createSlice({
  name: 'profileSwitch',
  initialState: initialProfileSwitchState,
  reducers: {
    setFetched: (state, { payload }) => {
      state.fetched = payload;
    },
    toggleProfileSwitchLoading: (state, { payload }) => {
      state.loading = payload;
    },
    toggleTableLoad: (state, action: KeyedAction<boolean>) => {
      const { key, value } = action.payload;
      state.entities[key].loading = value;
    },
    setSearchValue: (state, action: KeyedAction<string>) => {
      const { key, value } = action.payload;
      state.entities[key].search = value;
    },
    setActiveTab: (state, action) => {
      state.uiState.tabIndex = action.payload;
    },
    setTabKeys: (state, action) => {
      state.uiState.tabKeys = action.payload;
    },
    setPagination: (state, action: KeyedAction<Partial<ProfilePagination>>) => {
      const { key, value } = action.payload;
      state.entities[key].pagination = {
        ...state.entities[key].pagination,
        ...value,
      };
    },
    setSelectedEntity: (
      state,
      action: KeyedAction<
        string,
        Exclude<ProfileTypeKeys, 'myEntities' | 'officialReceiver'>
      >,
    ) => {
      const { key, value } = action.payload;
      state.entities[key].selectedUen = value;
    },
    saveSingleProfileTypeRecord: (
      state,
      action: PayloadAction<{
        key: ProfileTypeKeys;
        records: ProfileRecord[];
        pagination: Partial<ProfilePagination>;
      }>,
    ) => {
      const { key, records, pagination } = action.payload;
      state.entities[key].data = records;
      state.entities[key].pagination = {
        ...state.entities[key].pagination,
        ...pagination,
      };
    },
    saveProfileRecords: (
      state,
      action: PayloadAction<ProfileEntitiesObject>,
    ) => {
      const records = action.payload;
      (Object.keys(records) as ProfileTypeKeys[]).forEach((key) => {
        const data = records[key] as ProfileRecord[];
        if (
          key === 'myEntities' ||
          key === 'officialReceiver' ||
          state.entities[key].parentEntities
        ) {
          state.entities[key].data = data;
        } else {
          state.entities[key].parentEntities = data;
        }
      });
    },
    resetProfileSwitchState: () => ({ ...initialProfileSwitchState }),
  },
});

export const {
  setFetched,
  toggleProfileSwitchLoading,
  toggleTableLoad,
  setSearchValue,
  setActiveTab,
  setTabKeys,
  setPagination,
  setSelectedEntity,
  saveSingleProfileTypeRecord,
  saveProfileRecords,
  resetProfileSwitchState,
} = profileSwitchSlice.actions;

export default profileSwitchSlice.reducer;
